import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { FAVORITE_TYPE_LISTING } from '../../util/favorites';
import useFavoriteToggle from '../../util/hooks/useFavoriteToggle';
import RacingThemeFavoriteButton from '../RacingThemeFavoriteButton/RacingThemeFavoriteButton';

import css from './RacingThemeMemberIdentity.module.css';

const Icon = ({ name }) => {
  const common = {
    width: 14,
    height: 14,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
  };

  switch (name) {
    case 'members':
      return (
        <svg {...common}>
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      );
    case 'family':
    case 'subfamily':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="3" />
          <path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1" />
        </svg>
      );
    case 'races':
      return (
        <svg {...common}>
          <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
          <line x1="4" y1="22" x2="4" y2="15" />
        </svg>
      );
    case 'hq':
      return (
        <svg {...common}>
          <path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6" />
        </svg>
      );
    case 'territory':
      return (
        <svg {...common}>
          <path d="M12 22s7-7.5 7-13a7 7 0 1 0-14 0c0 5.5 7 13 7 13z" />
          <circle cx="12" cy="9" r="2.5" />
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
        </svg>
      );
  }
};

const MessageIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

/**
 * Member / seller identity band for profile and listing detail.
 */
const RacingThemeMemberIdentity = props => {
  const {
    identity,
    className,
    onMessage,
    showMessage = true,
    onFavorite = null,
    isFavorite: isFavoriteProp = null,
    favoriteId = null,
    favoriteType = FAVORITE_TYPE_LISTING,
    showFavorite = true,
  } = props;
  const { name, logoUrl, logoAlt, meta } = identity;
  const { isFavorite: checkIsFavorite, onToggleFavorite } = useFavoriteToggle();

  const isFavorite =
    typeof isFavoriteProp === 'boolean'
      ? isFavoriteProp
      : checkIsFavorite(favoriteType, favoriteId);

  const handleFavorite = event => {
    if (typeof onFavorite === 'function') {
      onFavorite(event);
      return;
    }
    if (favoriteId) {
      onToggleFavorite(favoriteType, favoriteId);
    }
  };

  const showActions = showMessage || showFavorite;

  return (
    <section className={classNames(css.root, className)} aria-label={`${name} profile`}>
      <div className={css.logoWrap}>
        <img className={css.logo} src={logoUrl} alt={logoAlt || name} />
      </div>
      <div className={css.main}>
        <h1 className={css.name}>{name}</h1>
        <dl className={css.metaGrid}>
          {meta.map(item => (
            <div key={`${item.label}-${item.value}`} className={css.metaItem}>
              <dt className={css.metaLabel}>
                <span className={css.metaIcon}>
                  <Icon name={item.icon} />
                </span>
                {item.label}
              </dt>
              <dd className={css.metaValue}>{item.value}</dd>
            </div>
          ))}
        </dl>
      </div>
      {showActions ? (
        <div className={css.actions}>
          {showMessage ? (
            <button className={css.messageBtn} type="button" onClick={onMessage}>
              <MessageIcon />
              Message Team
            </button>
          ) : null}
          {showFavorite ? (
            <RacingThemeFavoriteButton isFavorite={isFavorite} onClick={handleFavorite} />
          ) : null}
        </div>
      ) : null}
    </section>
  );
};

RacingThemeMemberIdentity.defaultProps = {
  className: null,
  onMessage: () => {},
  showMessage: true,
  onFavorite: null,
  isFavorite: null,
  favoriteId: null,
  favoriteType: FAVORITE_TYPE_LISTING,
  showFavorite: true,
};

RacingThemeMemberIdentity.propTypes = {
  className: PropTypes.string,
  favoriteId: PropTypes.string,
  favoriteType: PropTypes.string,
  identity: PropTypes.shape({
    name: PropTypes.string.isRequired,
    logoUrl: PropTypes.string.isRequired,
    logoAlt: PropTypes.string,
    meta: PropTypes.arrayOf(
      PropTypes.shape({
        icon: PropTypes.string,
        label: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      })
    ).isRequired,
  }).isRequired,
  onMessage: PropTypes.func,
  showMessage: PropTypes.bool,
  onFavorite: PropTypes.func,
  isFavorite: PropTypes.bool,
  showFavorite: PropTypes.bool,
};

export default RacingThemeMemberIdentity;
